import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DeliveryDate, EnquiryHeaderItem } from '../../models/data/enquiry.model';
import { OpenItdcrEvent } from '../../models/app/openItdcr.model';
import { BackendService } from '../../services/backend/backend.service';
import { TeiasoftLoaderService } from '../../services/teiasoft-bucket/teiasoft-loader.service';
import { TeiasoftToasterService } from '../../services/teiasoft-bucket/teiasoft-toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-modal-delivery-options',
  templateUrl: './modal-delivery-options.component.html',
  styleUrls: ['./modal-delivery-options.component.scss']
})
export class ModalDeliveryOptionsComponent implements OnInit {

  constructor(private backendService: BackendService, private loader: TeiasoftLoaderService, private toast: TeiasoftToasterService, private translateService: TranslateService) { }

  @Input() enquiryNo: string = "";
  @Input() item?: EnquiryHeaderItem;
  @Input() isMobile: boolean = false;
  @Input() theme: string = "silver";
  @Input() origin: string = '';
  @Input() readOnly: boolean = true;

  deliveryDatesCollection: DeliveryDate[] = [];
  minDate: Date = new Date();
  showItdcrViewerModal: boolean = false;
  selectedItdcrNo: string = '';

  @Output() onCancel: EventEmitter<any> = new EventEmitter();
  @Output() onOpenITDCR: EventEmitter<OpenItdcrEvent> = new EventEmitter();
  @Output() onSaveDatesCollection: EventEmitter<DeliveryDate[]> = new EventEmitter();

  onOpenITDCRClick() {
    this.onOpenITDCR.emit(
      {
        enquiryNo: this.enquiryNo,
        itemNo: this.item?.itemNo!,
        sourceEvent: 'D'
      });
  }

  ngOnInit(): void {
    this.deliveryDatesCollection = this.getInitialDeliveryDatesCollection(this.item);

    if (this.isItdcrSource && this.deliveryDatesCollection.length === 0) {
      this.deliveryDatesCollection.push(this.createEmptyDeliveryDate());
    }
  }

  onCancelClick() {
    this.onCancel.emit('');
  }



  buildDeliveryDatesCollection(data: any) {
    const deliveryDates = data["deliveryDatesCollection.deliveryNumberOfWeeks"]?.split(",");
    const deliveryDatesWithExtraLeadTime = data["deliveryDatesCollection.deliveryNumberOfWeeksWithExtraLeadTime"]?.split(",");
    const deliveryQuantities = data["deliveryDatesCollection.deliveryQuantity"]?.split(",");
    const deliveryQuantityUoms = data["deliveryDatesCollection.deliveryQuantityUom"]?.split(",");
    const deliveryWeeksOfYear = data["deliveryDatesCollection.deliveryWeekOfYear"]?.split(",");
    const deliveryWeeksOfYearWithExtraLeadTime = data["deliveryDatesCollection.deliveryWeekOfYearWithExtraLeadTime"]?.split(",");

    const hasWeekNumbers = deliveryDates && deliveryDates.length > 0;
    const hasWeekOfYear = deliveryWeeksOfYear && deliveryWeeksOfYear.length > 0;
    const hasQuantities = deliveryQuantities && deliveryQuantities.length > 0 && deliveryQuantityUoms && deliveryQuantityUoms.length > 0;

    if (!hasQuantities || (!hasWeekNumbers && !hasWeekOfYear)) return [];

    const length = Math.max(
      deliveryDates?.length ?? 0,
      deliveryDatesWithExtraLeadTime?.length ?? 0,
      deliveryQuantities?.length ?? 0,
      deliveryQuantityUoms?.length ?? 0,
      deliveryWeeksOfYear?.length ?? 0
    );

    return Array.from({ length }).map((_, index: number) => ({
      deliveryNumberOfWeeks: deliveryDates && deliveryDates[index] !== undefined ? Number(deliveryDates[index]) : undefined,
      deliveryNumberOfWeeksWithExtraLeadTime: deliveryDatesWithExtraLeadTime && deliveryDatesWithExtraLeadTime[index] !== undefined ? Number(deliveryDatesWithExtraLeadTime[index]) : undefined,
      deliveryQuantity: deliveryQuantities && deliveryQuantities[index] !== undefined ? Number(deliveryQuantities[index]) : undefined,
      deliveryQuantityUom: deliveryQuantityUoms && deliveryQuantityUoms[index] !== undefined ? deliveryQuantityUoms[index] : undefined,
      deliveryWeekOfYear: deliveryWeeksOfYear && deliveryWeeksOfYear[index] !== undefined ? deliveryWeeksOfYear[index] : undefined,
      deliveryWeekOfYearWithExtraLeadTime: deliveryWeeksOfYearWithExtraLeadTime && deliveryWeeksOfYearWithExtraLeadTime[index] !== undefined ? deliveryWeeksOfYearWithExtraLeadTime[index] : undefined,
    })).filter(item =>
      item.deliveryQuantity !== undefined ||
      item.deliveryWeekOfYear !== undefined ||
      item.deliveryNumberOfWeeks !== undefined ||
      item.deliveryNumberOfWeeksWithExtraLeadTime !== undefined ||
      item.deliveryWeekOfYearWithExtraLeadTime !== undefined
    );
  }


  formatWeeks(weeks: number): string {
    return `${weeks} week${weeks > 1 ? 's' : ''}`;
  }

  get isItdcrSource(): boolean {
    return String(this.item?.deliverySource ?? '').trim().toLowerCase() === 'itdcr';
  }

  get showItdcrHistoryLink(): boolean {
    const source = String(this.item?.deliverySource ?? '').trim().toLowerCase();
    return source === 'itdcr' || source === 'itr';
  }

  addDeliveryDateRow() {
    if (this.readOnly) return;
    this.deliveryDatesCollection = [...this.deliveryDatesCollection, this.createEmptyDeliveryDate()];
  }

  removeDeliveryDateRow(index: number) {
    if (this.readOnly) return;
    if (this.deliveryDatesCollection.length <= 1) return;
    this.deliveryDatesCollection = this.deliveryDatesCollection.filter((_, i) => i !== index);
  }

  onDeliveryWeekChange(value: string, index: number) {
    if (value === undefined || value === null) return;
    const normalizedWeek = this.normalizeWeekValue(value);
    this.deliveryDatesCollection[index].deliveryWeekOfYear = normalizedWeek;

    const extraLeadTime = Number(this.item?.extraLeadTime ?? 0);
    const baseWeekStart = this.getDateFromWeekLabel(normalizedWeek);

    if (!baseWeekStart || isNaN(extraLeadTime)) {
      this.deliveryDatesCollection[index].deliveryWeekOfYearWithExtraLeadTime = '';
      return;
    }

    const weekWithExtraLeadTime = this.addWeeks(baseWeekStart, extraLeadTime);
    this.deliveryDatesCollection[index].deliveryWeekOfYearWithExtraLeadTime = this.formatWeekLabel(weekWithExtraLeadTime);

  }

  onDeliveryQuantityChange(value: string, index: number) {
    if (value === undefined || value === null || value === '') {
      this.deliveryDatesCollection[index].deliveryQuantity = undefined;
      return;
    }

    const parsed = parseFloat(value.toString());
    this.deliveryDatesCollection[index].deliveryQuantity = isNaN(parsed) ? undefined : parsed;
  }

  canSave(): boolean {
    if (!this.deliveryDatesCollection || this.deliveryDatesCollection.length === 0) return false;

    if (this.hasDuplicateWeeks()) return false;

    return this.deliveryDatesCollection.every(item =>
      !!item.deliveryWeekOfYear &&
      item.deliveryQuantity !== undefined &&
      item.deliveryQuantity !== null &&
      !isNaN(Number(item.deliveryQuantity))
    );
  }

  onSaveClick() {

    if (this.hasDuplicateWeeks()) {
      this.toast.show('error', this.translateService.instant("enquiryBook.duplicateWeeksError"));
      return;
    }

    const preparedCollection = this.deliveryDatesCollection.map(item => ({
      ...item,
      deliveryQuantityUom: item.deliveryQuantityUom || this.item?.quantityUom || ''
    }));

    this.loader.start();

    this.backendService.updateItemDeliveryDates(this.enquiryNo, this.item!.itemNo!, preparedCollection).subscribe({
      next: () => {
        this.toast.show('success', this.translateService.instant("commons.success"));
      },
      error: (_err: any) => {
      },
      complete: () => this.loader.stop()
    }
    );


    this.onSaveDatesCollection.emit(preparedCollection);
  }

  onNavigateToItdcrHistory() {
    const itemNo = this.item?.itemNo;
    if (!this.enquiryNo || !itemNo || !this.showItdcrHistoryLink) return;

    this.loader.start();
    this.backendService.getItdcrNumberByItemAndCategory(this.enquiryNo, itemNo, 'D')
      .pipe(finalize(() => this.loader.stop()))
      .subscribe(res => {
        const itdcrNo = res?.itdcrNo?.trim?.() || '';
        if (!itdcrNo) {
          this.toast.show('info', this.translateService.instant('itdcr.notAvailableForItem'));
          return;
        }

        this.selectedItdcrNo = itdcrNo;
        this.showItdcrViewerModal = true;
      });
  }

  onCloseItdcrViewer() {
    this.showItdcrViewerModal = false;
    this.selectedItdcrNo = '';
  }

  private getInitialDeliveryDatesCollection(data?: EnquiryHeaderItem): DeliveryDate[] {
    if (!data) return [];

    if (data.deliveryDatesCollection && data.deliveryDatesCollection.length > 0) {
      return data.deliveryDatesCollection.map(dd => ({
        ...dd,
        deliveryQuantityUom: dd.deliveryQuantityUom || data.quantityUom || ''
      }));
    }

    const builtCollection = this.buildDeliveryDatesCollection(data);
    return builtCollection.map(dd => ({
      ...dd,
      deliveryQuantityUom: dd.deliveryQuantityUom || data.quantityUom || ''
    }));
  }

  private normalizeWeekValue(value: string): string {
    if (!value) return '';
    const trimmed = value.trim();

    if (/^\d{4}-W\d{2}$/.test(trimmed)) {
      const [year, weekPart] = trimmed.split('-W');
      const weekNumber = parseInt(weekPart, 10);
      const shortYear = year.slice(-2);
      const paddedWeek = weekNumber.toString().padStart(2, '0');
      return `W${paddedWeek}-${shortYear}`;
    }

    const match = /^W?(\d{1,2})[-/](\d{2,4})$/.exec(trimmed);
    if (match) {
      const weekNumber = parseInt(match[1], 10);
      const year = match[2];
      const shortYear = year.length === 4 ? year.slice(-2) : year;
      const paddedWeek = weekNumber.toString().padStart(2, '0');
      return `W${paddedWeek}-${shortYear}`;
    }

    return trimmed;
  }

  private getISOWeekStart(date: Date): Date {
    const temp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = temp.getUTCDay() || 7;
    temp.setUTCDate(temp.getUTCDate() - day + 1);
    return temp;
  }

  private getISOWeekNumber(date: Date): number {
    const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = tempDate.getUTCDay() || 7;
    tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
    return Math.ceil((((tempDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  }

  private formatWeekLabel(date: Date): string {
    const week = this.getISOWeekNumber(date).toString().padStart(2, '0');
    const year = date.getUTCFullYear().toString().slice(-2);
    return `W${week}-${year}`;
  }

  private getDateFromWeekLabel(label: string): Date | null {
    const match = /^W(\d{2})-(\d{2})$/.exec(label.trim());
    if (!match) return null;
    const week = parseInt(match[1], 10);
    const year = 2000 + parseInt(match[2], 10);
    const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
    const dayOfWeek = simple.getUTCDay();
    const isoWeekStart = new Date(simple);
    if (dayOfWeek <= 4) {
      isoWeekStart.setUTCDate(simple.getUTCDate() - simple.getUTCDay() + 1);
    } else {
      isoWeekStart.setUTCDate(simple.getUTCDate() + 8 - simple.getUTCDay());
    }
    return isoWeekStart;
  }

  private addWeeks(date: Date, weeks: number): Date {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + (weeks * 7));
    return result;
  }

  private getWeeksFromNow(targetWeekStart: Date): number {
    const nowWeekStart = this.getISOWeekStart(new Date());
    const diffMs = targetWeekStart.getTime() - nowWeekStart.getTime();
    const weeks = Math.ceil(diffMs / (7 * 24 * 60 * 60 * 1000));
    return Math.max(0, weeks);
  }

  hasDuplicateWeeks(): boolean {
    const weeks = this.deliveryDatesCollection
      .map(dd => dd.deliveryWeekOfYear ? this.normalizeWeekValue(dd.deliveryWeekOfYear) : '')
      .filter(w => !!w);

    const unique = new Set(weeks);
    return unique.size !== weeks.length;
  }

  formatQuantity(quantity?: number | null): string {
    if (quantity === null || quantity === undefined) return '';
    const num = Number(quantity);
    if (isNaN(num)) return '';
    return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  }

  private createEmptyDeliveryDate(): DeliveryDate {
    return {
      deliveryNumberOfWeeks: undefined,
      deliveryNumberOfWeeksWithExtraLeadTime: undefined,
      deliveryWeekOfYear: '',
      deliveryQuantity: undefined,
      deliveryQuantityUom: this.item?.quantityUom || ''
    };
  }
}
